import { Auth } from '@angular/fire/auth'
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatIcon } from '@angular/material/icon'
import { Router } from '@angular/router'
import { Title } from '@angular/platform-browser'

import { LoadingComponent } from '../../components/loading/loading.component'
import { ButtonComponent } from '../../components/button/button.component'
import { AuthService } from '../../services/auth.service'
import { MfaService } from '../../services/mfa.service'
import { getTitle } from '../../util/accessibility'
import { invalidStringValidator } from '../../directives/validator/custom-validator'

const VERIFICATION_CODE_REGEX = /^\d{6}$/

@Component({
  selector: 'app-sms-verification-page-page',
  standalone: true,
  imports: [LoadingComponent, ButtonComponent, MatIcon, ReactiveFormsModule],
  templateUrl: './sms-verification-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sms-verification-page.component.scss',
})
export class SmsVerificationPageComponent implements OnInit {
  isLoading = false
  verificationCode = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      invalidStringValidator(VERIFICATION_CODE_REGEX),
    ],
  })

  smsVerificationForm = new FormGroup({
    verificationCode: this.verificationCode,
  })

  phoneNumber = ''

  constructor(
    private auth: Auth,
    private router: Router,
    private authService: AuthService,
    private mfaService: MfaService,
    private titleService: Title,
  ) {
    this.titleService.setTitle(getTitle('SMS認証'))
  }

  ngOnInit(): void {
    const loginResult = this.authService.getLoginResult()
    if (!this.auth.currentUser && !loginResult?.resolver) {
      this.router.navigate(['/login'])
      return
    }

    this.phoneNumber = this.authService.getPhoneNumber()
    if (!this.phoneNumber) {
      this.router.navigate(['/auth/phone-setup'])
      return
    }
  }

  backToPhoneSetup() {
    const resolver = this.authService.getLoginResult()?.resolver
    if (resolver && resolver.hints.length >= 2) {
      this.router.navigate(['/auth/mfa-verify-select'])
      return
    }
    this.router.navigate(['/auth/phone-setup'])
  }

  sendVerificationCodeSubmit = async (event: Event) => {
    event.preventDefault()

    if (this.smsVerificationForm.invalid) {
      return
    }

    const verificationCode = this.verificationCode.value
    const loginResult = this.authService.getLoginResult()
    const verificationId = this.authService.getVerificationId()

    if (!this.auth.currentUser && !loginResult?.resolver) {
      this.router.navigate(['/login'])
      return
    }

    this.isLoading = true
    try {
      if (loginResult?.resolver) {
        await this.mfaService.verifyCode(
          loginResult.resolver,
          verificationId,
          verificationCode,
        )
      } else if (this.auth.currentUser) {
        await this.mfaService.confirmEnrollment(
          this.auth.currentUser,
          verificationId,
          verificationCode,
        )
      } else {
        this.router.navigate(['/login'])
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      let errorMessage = 'エラーが発生しました。'
      if (error.code === 'auth/requires-recent-login') {
        this.isLoading = false
        if (this.authService.isFromMfaSettings()) {
          await this.authService.startReauthentication({ type: 'add-sms' })
        } else {
          await this.authService.signOutPreservingReturnUrl()
          this.authService.redirectToLogin({ reason: 'reauth' })
        }
        return
      }
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'SMS認証コードが無効です。'
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'SMS認証コードの有効期限が切れています。'
      }
      alert(errorMessage)
      this.isLoading = false
      throw error
    }

    this.authService.clearMfaEnrollmentOrigin()
    const returnUrl = this.authService.consumeReturnUrl() || '/home'
    this.authService.clearLoginState()
    this.router.navigateByUrl(returnUrl)
  }
}
